import React, { useState } from 'react';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import FormDrawer from '../../../components/ui/FormDrawer';
import ErrorModal from '../../../components/ui/modal/ErrorModal';
import { useDrawerStore } from '../../../store';
import {
  useCreateData,
  useDeliveryReceiptStore,
  useFetchData,
  useUpdateData,
} from '../../../hooks';
import { BaseModel, FormField, PageableResponse } from '../../../types';
import { Resource } from '../../../lib/enums';
import { dateFormat } from '../../../lib/utils';

/**
 * The header of a delivery receipt: when, where to, and who carried it.
 *
 * The materials are added on the receipt itself, because a receipt raised from
 * a receiving report already arrives with its lines and only needs a
 * destination.
 */
const DeliveryReceiptForm = ({
  api,
  moduleName,
}: {
  api: string;
  moduleName: string;
}) => {
  const drawerStore = useDrawerStore();
  const drStore = useDeliveryReceiptStore();
  const [spinning, setSpinning] = useState(false);
  const [isErrorOpen, setErrorOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  const { data: locationData, isLoading: isLocLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/location/locations', { resource: Resource.NAME, length: 999999 });

  const { mutate: create } = useCreateData(api);
  const { mutate: update } = useUpdateData(api);

  const onFinish = (values: {
    destination?: string;
    location?: string;
    requiredDate?: any;
    [k: string]: any;
  }) => {
    setSpinning(true);
    const find = (id?: string) => locationData?.data?.find((o) => o.id === id);
    // Merged onto the record, not sent alone: an update maps the payload over
    // the whole entity, so a partial body would drop the movements and the id.
    const payload = {
      ...drStore.data,
      ...values,
      id: drStore.data?.id,
      destination: find(values.destination),
      location: find(values.location),
      requiredDate: values.requiredDate
        ? values.requiredDate.format(dateFormat)
        : drStore.data?.requiredDate,
    };

    const onError = (error: any) => {
      setSpinning(false);
      setErrorMessage(
        error?.response?.data?.message ?? 'Could not save the delivery receipt.',
      );
      setErrorOpen(true);
    };
    const onSuccess = () => {
      setSpinning(false);
      drStore.setData(null);
    };

    if (drStore.data?.id) {
      update({ id: drStore.data.id, form: payload }, { onError, onSuccess });
    } else {
      create(payload, { onError, onSuccess });
    }
  };

  const formFields: FormField[] = [
    ...(drStore.data?.id
      ? [
          {
            name: 'code',
            label: 'DR No.',
            disabled: true,
            defaultValue: drStore.data?.code,
          },
        ]
      : []),
    {
      name: 'requiredDate',
      label: 'Delivery Date',
      type: 'datepicker',
      defaultValue: drStore.data?.requiredDate
        ? dayjs(drStore.data.requiredDate)
        : dayjs(),
    },
    {
      name: 'destination',
      label: 'Deliver To',
      type: 'select',
      rules: [{ required: true, message: 'Please choose where this is going' }],
      values: locationData?.data,
      isLoading: isLocLoading,
      defaultValue: drStore.data?.destination?.id,
    },
    {
      name: 'location',
      label: 'From',
      type: 'select',
      allowClear: true,
      values: locationData?.data,
      isLoading: isLocLoading,
      defaultValue: drStore.data?.location?.id,
    },
    {
      name: 'remarks',
      label: 'Carried By',
      defaultValue: drStore.data?.remarks,
    },
    {
      name: 'notes',
      label: 'Notes',
      type: 'textarea',
      defaultValue: drStore.data?.notes,
    },
  ];

  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <FormDrawer
        isOpen={drawerStore.isOpen}
        onClose={drawerStore.onClose}
        data={drStore.data}
        moduleName={moduleName}
        setData={drStore.setData}
        onFinish={onFinish}
        formFields={formFields}
      />
      <ErrorModal
        visible={isErrorOpen}
        message={errorMessage}
        onClose={() => setErrorOpen(false)}
      />
    </>
  );
};

export default DeliveryReceiptForm;
